/**
 * Stock Synchronization Utility
 * Uses Page Visibility API - refreshes data when user switches back to the tab
 * Zero background traffic, very efficient
 */

const STORAGE_KEY = 'stock-last-update';

// Track callbacks for visibility change
const callbacks = [];
let isListening = false;

// Handle visibility change - when user comes back to tab, check for updates
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    
    // Check if there were any updates while tab was hidden
    const lastUpdate = localStorage.getItem(STORAGE_KEY);
    
    // Notify all callbacks to refresh data
    callbacks.forEach(cb => {
      try {
        cb({ timestamp: lastUpdate, source: 'visibility-change' });
      } catch (err) {
      }
    });
  }
};

// Start listening for visibility changes
const startListening = () => {
  if (isListening) return;
  
  document.addEventListener('visibilitychange', handleVisibilityChange);
  isListening = true;
};

// Broadcast a stock change event (called when admin makes changes)
export const broadcastStockChange = (productId, newData) => {
  const timestamp = Date.now().toString();
  const payload = { productId, ...newData, timestamp };
  
  // Update localStorage with new timestamp
  try {
    localStorage.setItem(STORAGE_KEY, timestamp);
  } catch (e) {
    // localStorage may be unavailable (private browsing)
  }
  
  // Dispatch CustomEvent for same-page/same-tab updates
  window.dispatchEvent(new CustomEvent('stock-change', { detail: payload }));
};

// Subscribe to stock changes
export const subscribeToStockChanges = (callback) => {
  
  // Add callback to list
  callbacks.push(callback);
  
  // Start listening for visibility changes
  startListening();
  
  // Listen for same-page CustomEvent (for updates within same tab)
  const eventListener = (event) => {
    callback(event.detail);
  };
  window.addEventListener('stock-change', eventListener);
  
  // Listen for storage events (fires when another tab modifies localStorage)
  const storageListener = (event) => {
    if (event.key === STORAGE_KEY && event.newValue) {
      callback({ timestamp: event.newValue, source: 'storage-event' });
    }
  };
  window.addEventListener('storage', storageListener);
  
  
  // Return unsubscribe function
  return () => {
    const index = callbacks.indexOf(callback);
    if (index > -1) callbacks.splice(index, 1);
    window.removeEventListener('stock-change', eventListener);
    window.removeEventListener('storage', storageListener);
  };
};

// Legacy support functions
export const triggerLocalRefresh = () => {
  window.dispatchEvent(new CustomEvent('stock-local-refresh', { detail: { timestamp: Date.now() } }));
};

export const subscribeToLocalRefresh = (callback) => {
  const handler = (event) => callback(event.detail);
  window.addEventListener('stock-local-refresh', handler);
  return () => window.removeEventListener('stock-local-refresh', handler);
};
